import { useTranslation } from 'react-i18next';
import { useAuthStore } from '@/store/authStore';
import { useSystemStore } from '@/store/systemStore';
import { useUIStore } from '@/store/uiStore';
import { useOrganizationStore } from '@/store/organizationStore';
import { Search, MegaphoneIcon, HelpCircle, ClipboardList, Sun, Moon, Menu } from 'lucide-react';
import {
  LogOut,
  User as UserIcon,
  Settings
} from 'lucide-react';
import { useState } from 'react';
import GlobalSearchDialog, { preloadGlobalSearch } from './LazyGlobalSearchDialog';
import { Link } from 'react-router-dom';
import { useOpenFeedback } from '@/hooks/useOpenFeedback';
import { cn } from '@/lib/utils';
import { UserAvatar } from '@/components/ui/UserAvatar';
import LanguageSwitcher from '../LanguageSwitcher';
import NotificationBell from '@/components/notifications/NotificationBell';
import MessagesIcon from '@/components/messaging/MessagesIcon';

const ENV_STYLES = {
  development: 'bg-[var(--bp-accent-soft)] border-[var(--bp-accent)]/30',
  testing:     'bg-fuchsia-50/50 dark:bg-fuchsia-950/40 border-fuchsia-200 dark:border-fuchsia-800',
  production:  'bg-card border-border',
};

export default function Topbar() {
  const { t } = useTranslation();
  const { user, logout } = useAuthStore();
  const { settings } = useSystemStore();
  const { isHelpOpen, toggleHelp, isTestPanelOpen, toggleTestPanel, isDarkMode, toggleDarkMode, setMobileSidebarOpen } = useUIStore();
  const { currentOrganization } = useOrganizationStore();
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isGlobalSearchOpen, setIsGlobalSearchOpen] = useState(false);

  const env = (settings?.environment_type ?? 'production') as keyof typeof ENV_STYLES;
  const headerStyle = ENV_STYLES[env] ?? ENV_STYLES.production;

  /**
   * Podnapis pod imenom pove **vlogo**, ne golega »Uporabnik«.
   *
   * Vrstni red je namerno tak: `is_site_admin` je platformska vloga in prevlada
   * nad članstvom (isto ločnico dela `usePermissions`, kjer site admin prehiti
   * vsako preverbo pravic). Šele nato pride vloga v **trenutni** organizaciji
   * (`owner` / `admin` / `manager` / `member` — `MEMBER_ROLE_CHOICES` v
   * `apps/core/models.py:64`). Ker se podnapis veže na izbrano organizacijo, se
   * spremeni ob preklopu organizacije — to je pravilno, saj je isti človek
   * lahko lastnik ene in član druge.
   *
   * `manager` je **namerno izpuščen** (odločitev Alena, 8.9.2026): »Vodja
   * projektov« je vloga, ki pomeni nekaj določenega v Quantify, ne pa nujno
   * tudi v ostalih modulih (BidFlow, SitePro). Glava je čez vse module ena
   * sama, zato tam ne sme trditi več, kot velja povsod — vodja projektov
   * dobi splošni `common.user`, tako kot vsaka druga neuvrščena vloga.
   *
   * Fallback ostane `common.user` za stanje, ko organizacija še ni naložena ali
   * je uporabnik brez članstva (npr. sysadmin brez organizacije).
   */
  const ORG_ROLE_LABEL_KEYS: Record<string, string> = {
    owner: 'topbar.role_owner',
    admin: 'topbar.role_admin',
    member: 'topbar.role_member',
  };
  const roleLabel = user?.is_site_admin
    ? t('topbar.role_sysadmin')
    : t(ORG_ROLE_LABEL_KEYS[currentOrganization?.role ?? ''] ?? 'common.user');

  // T2410: logika je v `useOpenFeedback` — deli si jo s stisnjeno glavo urejevalnika.
  const openFeedback = useOpenFeedback();

  return (
    /* T1811: odmik je odziven. Brezpogojni `px-8` je vzel 64 px na zaslonu, ki
       jih nima — pri 375 px je bila desna skupina izmerjena na 453 px proti
       311 px razpoložljivim. Nad `lg` se odmik vrne, ker je tam prostora dovolj
       in je zračnost del videza. */
    <header className={cn("h-16 border-b flex items-center justify-between px-4 lg:px-8 sticky top-0 z-[50] transition-colors duration-300", headerStyle)}>
      <div className="flex items-center gap-2 min-w-0 flex-1">
        {/* T1263: pod `md` je stranska vrstica skrita — hamburger jo odpre kot
            overlay. Na md+ gumba ni; tam stoji sticky Sidebar s svojim toggle. */}
        <button
          type="button"
          onClick={() => setMobileSidebarOpen(true)}
          title={t('sidebar.open_menu')}
          aria-label={t('sidebar.open_menu')}
          className="md:hidden shrink-0 p-2 -ml-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
        >
          <Menu className="h-5 w-5" />
        </button>
        {/* T1963: pod `sm` je to ikona, nad njim polje — ena kontrola, dve
            obliki. Pri 375 px je bilo za besedilo mesta za vnos 52 px od
            potrebnih 230 (vidnih ~22 %), torej je na zaslonu stalo »Išč« brez
            vidnega konca. ⚠️ Ta popravek je bil pri T1811 **z meritvijo
            ovržen kot rešitev prelivanja** — tam ni mogel pomagati, ker je bil
            iskalnik skrčen na 0 px in bi mu ikona 36 px celo dodala. Kot
            popravek videza pa drži: ikona pove vse, odrezano polje manj. */}
        <button
          type="button"
          onClick={() => setIsGlobalSearchOpen(true)}
          onMouseEnter={preloadGlobalSearch}
          onFocus={preloadGlobalSearch}
          /* T1963: `min-w-0` velja šele od `sm`. Pod njim je gumb ikona in mora
             obdržati svojo min-content širino — z `min-w-0` se je stisnil na
             24 px ob 28 px vsebine (izmerjeno), kar je pod vsakim razumnim
             ciljem za dotik. Nad `sm` je spet polje, ki se sme krčiti. */
          className="relative shrink-0 sm:shrink sm:w-96 max-w-full sm:min-w-0 text-left"
          title={t('common.global_search_title')}
          aria-label={t('common.global_search_title')}
        >
          <span className="sm:hidden flex items-center p-2 -ml-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors">
            <Search className="h-5 w-5" />
          </span>
          <span className="hidden sm:flex absolute inset-y-0 left-0 pl-3 items-center text-muted-foreground pointer-events-none">
            <Search className="h-4 w-4" />
          </span>
          <span className="hidden sm:block w-full pl-10 pr-3 py-2 bg-background text-muted-foreground border border-border rounded-lg text-body truncate">
            {t('common.search_placeholder')}
          </span>
        </button>
      </div>
      <GlobalSearchDialog
        open={isGlobalSearchOpen}
        onClose={() => setIsGlobalSearchOpen(false)}
        organizationId={currentOrganization?.id}
      />

      {/* T1811: razmik je odziven, kontrole pa ostanejo vse.
          Izmerjeno 21.9.2026 v brskalniku: pri 375 px je ta skupina 453 px
          široka ob 311 px razpoložljivih, pri 768 px 514 ob 448 — presežek 110
          oz. 66 px, zato sta »Preklopi na temni način« in »Uporabniški meni«
          ležala zunaj zaslona (365→401 in 442→486) in do odjave ni bilo poti.
          Krivec ni bila nobena posamezna kontrola, ampak praznina med njimi:
          `gap-4` je med devetimi 128 px. Otroci te skupine nimajo `min-w-0`,
          zato se ne more skrčiti pod min-content — levi del vrstice (iskalnik)
          je bil ob tem izmerjen na **0 px**, torej je popuščal že do konca in
          njegovo krčenje ni bilo več na voljo kot vzvod. */}
      <div className="flex items-center gap-1 sm:gap-2 lg:gap-4">
        {/* T1963: pod `sm` se umakne v uporabniški meni. Izmerjeno pri 375 px:
            levi del vrstice dobi 64 px v produkciji (24 z razvojnim
            pripomočkom), hamburger sam vzame 36 in iskalnik jih potrebuje 36 —
            zato je lupa ostala nevidna, dokler se ni nekaj umaknilo. Izbrana je
            povratna informacija, ker ni vsakodnevni ukaz, iskanje pa je
            (odločitev Alena). Ne izgine — pot je spodaj v meniju. */}
        <button
          onClick={openFeedback}
          title={t('topbar.feedback')}
          aria-label={t('topbar.feedback')}
          className="hidden sm:flex p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
        >
          <MegaphoneIcon className="h-5 w-5" />
        </button>

        {env !== 'production' && (
          <button
            onClick={() => toggleTestPanel()}
            title={t('topbar.test_panel')}
            aria-label={t('topbar.test_panel')}
            className={cn(
              "p-2 transition-colors rounded-lg",
              isTestPanelOpen ? "bg-[var(--bp-accent-soft)] text-[var(--bp-accent-text)]" : "text-muted-foreground hover:text-foreground hover:bg-accent"
            )}
          >
            <ClipboardList className="h-5 w-5" />
          </button>
        )}

        <MessagesIcon />

        <NotificationBell />

        <button
          onClick={() => toggleHelp()}
          title={t('common.help')}
          aria-label={t('common.help')}
          className={cn(
            "p-2 transition-colors rounded-lg",
            isHelpOpen ? "bg-[var(--bp-accent-soft)] text-[var(--bp-accent-text)]" : "text-muted-foreground hover:text-foreground hover:bg-accent"
          )}
        >
          <HelpCircle className="h-5 w-5" />
        </button>

        {/* T1811: navpični ločnici sta okras; pod `sm` vzameta ~26 px, ki jih vrstica nima. */}
        <div className="hidden sm:block h-8 w-px bg-border mx-1" />

        <LanguageSwitcher variant="light" />

        <button
          onClick={toggleDarkMode}
          title={isDarkMode ? t('topbar.light_mode') : t('topbar.dark_mode')}
          aria-label={isDarkMode ? t('topbar.light_mode') : t('topbar.dark_mode')}
          className="p-2 text-muted-foreground hover:text-foreground hover:bg-accent rounded-lg transition-colors"
        >
          {isDarkMode ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
        </button>

        {/* T1811: navpični ločnici sta okras; pod `sm` vzameta ~26 px, ki jih vrstica nima. */}
        <div className="hidden sm:block h-8 w-px bg-border mx-1" />

        <div className="relative">
          <button
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            aria-label={t('topbar.user_menu')}
            aria-expanded={isUserMenuOpen}
            className="flex items-center gap-3 pl-2 pr-1 py-1 hover:bg-accent rounded-full transition-colors"
          >
            <div className="text-right hidden sm:block">
              <p className="text-body font-medium text-foreground">{user?.first_name} {user?.last_name}</p>
              <p className="text-caption text-muted-foreground">{roleLabel}</p>
            </div>
            <UserAvatar
              size="md"
              name={`${user?.first_name ?? ''} ${user?.last_name ?? ''}`.trim()}
              avatar={user?.avatar}
              colorClass="bg-[var(--bp-accent-soft)] text-[var(--bp-accent-text)] border border-[var(--bp-accent)]/30"
              className="flex-shrink-0"
            />
          </button>

          {isUserMenuOpen && (
            <>
              <div 
                className="fixed inset-0 z-[40]" 
                onClick={() => setIsUserMenuOpen(false)}
              />
              <div className="absolute right-0 mt-2 w-48 bg-card border border-border rounded-md shadow-lg z-[50] py-1">
                <Link 
                  to="/profile" 
                  onClick={() => setIsUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-body text-foreground hover:bg-accent"
                >
                  <UserIcon className="h-4 w-4" />
                  {t('common.profile')}
                </Link>
                <Link 
                  to="/settings" 
                  onClick={() => setIsUserMenuOpen(false)}
                  className="w-full flex items-center gap-2 px-4 py-2 text-body text-foreground hover:bg-accent"
                >
                  <Settings className="h-4 w-4" />
                  {t('common.settings')}
                </Link>
                {/* T1963: nadomestna pot do povratne informacije, dokler je
                    njen gumb umaknjen iz vrstice. Nad `sm` je gumb spet tam,
                    zato ta vnos odpade — dve poti do istega ukaza v istem
                    stanju sta šum (isto pravilo kot pri T1133/T1744). */}
                <button
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    void openFeedback();
                  }}
                  className="sm:hidden w-full flex items-center gap-2 px-4 py-2 text-body text-foreground hover:bg-accent"
                >
                  <MegaphoneIcon className="h-4 w-4" />
                  {t('topbar.feedback')}
                </button>
                <div className="h-px bg-[var(--bp-border)] my-1" />
                <button 
                  onClick={logout}
                  className="w-full flex items-center gap-2 px-4 py-2 text-body text-red-600 hover:bg-red-50 dark:hover:bg-red-950/40"
                >
                  <LogOut className="h-4 w-4" />
                  {t('common.logout')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
